/**
 * Tom & Sawyer — cancellation retention overlay for the Awtomic customer portal.
 *
 * Replaces the "Before you go ..." interstitial that used to live in
 * subscription-portal-enhancement.js as cloneModal(). That version deep-cloned
 * Awtomic's React-rendered modal, rewrote its innerHTML, and appended the clone
 * back into React's tree. On 2026-07-20 that class of DOM surgery took the whole
 * portal down: React's fiber still referenced nodes we had replaced, so its next
 * commit threw
 *   NotFoundError: The node to be removed is not a child of this node
 * and Awtomic's error boundary rendered "Something went wrong".
 *
 * DESIGN RULE FOR THIS FILE: only ever ADD nodes we own, and only ever interact
 * with Awtomic's nodes by reading them or calling .click() on them. Never write
 * innerHTML/innerText/style onto an Awtomic node, never remove/move/clone one.
 * Adding a node React never adopted is safe; taking one away is not.
 *
 * How it works:
 *   1. Watch .chakra-portal for Awtomic's cancellation modal appearing.
 *   2. Render OUR overlay on top of it (higher z-index + own backdrop), so
 *      Awtomic's modal is visually covered without being touched.
 *   3. "Pause instead" closes their modal and clicks their pause button.
 *      "I'd still like to cancel" just removes our overlay, revealing their
 *      modal so the normal flow continues.
 *
 * Detection is by TEXT, not by emotion class hashes. The old code keyed off
 * ".css-1e6mct9", which is a generated Chakra/emotion hash that changes on any
 * Awtomic rebuild — and Awtomic now has someone actively working on the portal.
 */
(function () {
  'use strict';

  if (window.__tsRetentionModalLoaded) return;
  window.__tsRetentionModalLoaded = true;

  // Order-independent page gate. The old test was
  //   href.indexOf('bundle-subscriptions?subscriptionId') > -1
  // which silently required subscriptionId to be the FIRST query parameter, so
  // ?authToken=...&subscriptionId=... never matched. See the 2026-07-20 QA note
  // in subscription-portal-enhancement for the full story.
  if (
    window.location.pathname.indexOf('/tools/bundle-subscriptions') === -1 ||
    !new URLSearchParams(window.location.search).get('subscriptionId')
  ) {
    return;
  }

  var OVERLAY_ID = 'ts-retention-overlay';
  var BRAND = '#034956';
  var INK = '#383A35';

  function log(msg) {
    if (window.TSRetentionDebug) console.log('Tom & Sawyer Retention: ' + msg);
  }

  /**
   * Awtomic's cancel modal, identified by content rather than class hash.
   * Returns null when the visible modal is something else (edit box, pause
   * confirmation, address form, …) so we don't hijack unrelated dialogs.
   */
  function findCancelModal() {
    var contents = document.querySelectorAll('.chakra-portal .chakra-modal__content');
    for (var i = 0; i < contents.length; i++) {
      var node = contents[i];
      var text = (node.textContent || '').toLowerCase();

      // Reject anything that is about pausing. A pause-confirmation dialog
      // almost always carries a "Cancel" dismiss button AND the word
      // "subscription", so a naive cancel+subscription test false-positives on
      // it — and hijacking the pause dialog with a "pause instead?" prompt
      // would be absurd. Verified against the live modal, whose heading is
      // "Reason for cancelling".
      if (text.indexOf('pause') > -1) continue;

      // Strong signal: Awtomic's actual cancellation-reason step.
      if (text.indexOf('reason for cancel') > -1) return node;

      // Weaker fallback, in case they reword the heading. Still requires an
      // explicit cancellation phrase rather than a bare "cancel", so a stray
      // "Cancel" button cannot trigger us.
      if (
        text.indexOf('cancel your subscription') > -1 ||
        text.indexOf('cancel subscription') > -1 ||
        text.indexOf('cancelling') > -1 ||
        text.indexOf('canceling') > -1
      ) {
        return node;
      }
    }
    return null;
  }

  function pauseButton() {
    return document.querySelector('.bundle-button--pause');
  }

  function closeButtonWithin(modal) {
    return modal.querySelector('.chakra-modal__close-btn');
  }

  function styleEl(el, styles) {
    for (var k in styles) if (Object.prototype.hasOwnProperty.call(styles, k)) el.style[k] = styles[k];
  }

  function makeButton(label, primary) {
    var b = document.createElement('button');
    b.type = 'button';
    b.textContent = label;
    styleEl(b, {
      display: 'block',
      width: '100%',
      padding: '13px 20px',
      marginTop: '10px',
      borderRadius: '6px',
      fontSize: '15px',
      fontWeight: primary ? '600' : '500',
      cursor: 'pointer',
      border: primary ? 'none' : '1px solid #cfd6d4',
      background: primary ? BRAND : 'transparent',
      color: primary ? '#ffffff' : '#6b706d',
      lineHeight: '1.3'
    });
    return b;
  }

  function removeOverlay() {
    var existing = document.getElementById(OVERLAY_ID);
    // Our own node — safe to remove, React never adopted it.
    if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
  }

  function buildOverlay(cancelModal) {
    var overlay = document.createElement('div');
    overlay.id = OVERLAY_ID;
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Before you go');
    styleEl(overlay, {
      position: 'fixed',
      inset: '0',
      // Chakra modals sit around 1400; stay above without being absurd.
      zIndex: '2147483000',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      // Darker than a normal scrim: Awtomic's own modal sits underneath at
      // z-index 1400 and its white panel reads through a light backdrop,
      // which made the interstitial look like it was part of their dialog.
      background: 'rgba(0,0,0,0.72)',
      padding: '20px',
      overflowY: 'auto'
    });

    var card = document.createElement('div');
    styleEl(card, {
      background: '#ffffff',
      borderRadius: '10px',
      maxWidth: '480px',
      width: '100%',
      padding: '28px',
      boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
      fontFamily: 'inherit',
      color: INK,
      boxSizing: 'border-box'
    });

    var h = document.createElement('h2');
    h.textContent = 'Before you go …';
    styleEl(h, { margin: '0 0 14px', fontSize: '22px', fontWeight: '600', color: INK, lineHeight: '1.25' });

    var p1 = document.createElement('p');
    p1.textContent =
      'We understand the need to take a break from your regular routine — we all need a paws once in a while!';
    var p2 = document.createElement('p');
    p2.textContent =
      "Are you travelling, have too much food in your freezer, or need to budget this month? You can skip your next delivery, or pause your autoship and restart whenever you're ready.";
    [p1, p2].forEach(function (p) {
      styleEl(p, { margin: '0 0 12px', fontSize: '15px', lineHeight: '1.5', color: '#4a4f4c' });
    });

    var pauseBtn = makeButton('Pause my autoship', true);
    var continueBtn = makeButton("I'd still like to cancel", false);

    pauseBtn.addEventListener('click', function (e) {
      e.preventDefault();
      var target = pauseButton();
      var closeBtn = closeButtonWithin(cancelModal);
      removeOverlay();
      // Close Awtomic's cancel modal, then trigger their pause control.
      // .click() only dispatches an event — it does not restructure their DOM.
      if (closeBtn) closeBtn.click();
      if (target) {
        setTimeout(function () { target.click(); }, 120);
      } else {
        log('pause button not found — closed cancel modal only');
      }
    });

    continueBtn.addEventListener('click', function (e) {
      e.preventDefault();
      // Just get out of the way; Awtomic's modal is untouched underneath.
      removeOverlay();
    });

    card.appendChild(h);
    card.appendChild(p1);
    card.appendChild(p2);
    card.appendChild(pauseBtn);
    card.appendChild(continueBtn);
    overlay.appendChild(card);

    // Clicking the backdrop behaves like "still like to cancel" (dismiss ours).
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) removeOverlay();
    });

    return overlay;
  }

  function maybeShow() {
    if (document.getElementById(OVERLAY_ID)) return;
    var cancelModal = findCancelModal();
    if (!cancelModal) return;
    if (cancelModal.getAttribute('data-ts-retention-shown') === 'true') return;
    // Marker attribute on their node: an attribute write, not a structural
    // change. React will not throw on this, and worst case a re-render drops
    // the attribute and we simply offer the interstitial again.
    try { cancelModal.setAttribute('data-ts-retention-shown', 'true'); } catch (_) {}
    log('cancel modal detected — showing retention overlay');
    document.body.appendChild(buildOverlay(cancelModal));
  }

  function start() {
    var observer = new MutationObserver(function () { maybeShow(); });
    observer.observe(document.body, { childList: true, subtree: true });
    maybeShow();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
